/**
 * The public home page body (#2443, hero round 2 in #2461): the "Phones in
 * conversation" hero with glass bubbles, four value tiles, one capture and
 * the decision line. Source board:
 * `docs/design/rebrand-concepts/2026-10-16/h2-phones-in-conversation.html`.
 *
 * Reduced motion is read from the OS setting directly, not through
 * `motion`: the sky is CSS keyframes and the screens use the Web Animations
 * API (#2490), and both must follow the setting.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { REDUCED_MOTION_QUERY, prefersReducedMotion } from "../../lib/motionTokens";
import { TariiWord } from "./TariiWord";
import {
  LOOP_S,
  type Track,
  cameraFrames,
  cursorFrames,
  layerFrames,
  tapFrames,
} from "./heroJourney";

import { StoryRun, StorySummary } from "./StoryRun";
import "./home-still-water.css";

export const STAGE_LABEL =
  "The lead sets the rules, the stations and who may work where. A member requests leave on their phone and it appears at once on the lead's laptop and phone; the lead accepts it, solves the dates, moves one shift and publishes. Another member reports off sick, the lead seats a cover, and every phone receives its shifts.";

/** True when the visitor asks for reduced motion; follows later changes. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/**
 * The three phones (#2490). Each bubble pops when its action happens. The
 * second phone is Gary by day and Hakim at night (`alt`): Hakim is told he
 * covers the day Gary reported off sick.
 */
export type Cast = "maya" | "jo" | "alex" | "sam";
/**
 * The desk calendar beside the monitor, as on How it works: Tuesday 20
 * October through the morning, then Thursday 22 for the sick day.
 */
export function DeskCalendar() {
  return (
    <div className="hp-deskcal" aria-hidden="true">
      <span className="hp-deskcal-rings" />
      <span className="hp-deskcal-page hp-deskcal-a">
        <small>Oct</small>
        <b>20</b>
        <small>Tue</small>
      </span>
      <span className="hp-deskcal-page hp-deskcal-b">
        <small>Oct</small>
        <b>22</b>
        <small>Thu</small>
      </span>
    </div>
  );
}

/**
 * A How it works character (`/site/characters/<cast>/<pose>.svg`). `alt` is
 * the stand-in who takes over at night (Hakim covers Gary's sick day).
 */
export function Character({
  cast,
  pose,
  alt,
}: { cast: Cast; pose: string; alt?: Cast | undefined }) {
  return (
    <div className="hp-char">
      <img src={`/site/characters/${cast}/${pose}.svg`} alt="" draggable={false} />
      {alt ? (
        <img
          className="hp-char-alt"
          src={`/site/characters/${alt}/${pose}.svg`}
          alt=""
          draggable={false}
        />
      ) : null}
    </div>
  );
}

/**
 * One screen's journey (#2490): a camera over the page captures, one layer
 * per page change, and a ripple per tap. The Web Animations API plays the
 * keyframes from `heroJourney.ts` on the shared loop. Reduced motion shows the
 * page marked `still`; a browser without `element.animate` keeps the first page.
 */
export function JourneyScreen({
  track,
  reduced,
  pointer,
  startAt = 0,
  paused = false,
  seekTo,
}: {
  track: Track;
  reduced: boolean;
  /** Seconds into the loop to start from (the story run starts each screen at its beat). */
  startAt?: number;
  /** Hold the screen on its current frame (the story run pauses quiet screens). */
  paused?: boolean;
  /** Jump to this many seconds into the loop when the screen plays again. */
  seekTo?: number | undefined;
  /** The pointer over this screen: a mouse on the monitor, a finger on a phone. */
  pointer: "mouse" | "finger";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const animsRef = useRef<Animation[]>([]);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    const root = ref.current;
    if (reduced || !root || typeof root.animate !== "function") return;
    const opts: KeyframeAnimationOptions = {
      duration: LOOP_S * 1000,
      iterations: Number.POSITIVE_INFINITY,
    };
    const anims: Animation[] = [];
    const cam = root.querySelector<HTMLElement>(".hp-cam");
    if (cam) anims.push(cam.animate(cameraFrames(track) as Keyframe[], opts));
    root.querySelectorAll<HTMLElement>(".hp-page").forEach((el, i) => {
      anims.push(el.animate(layerFrames(track, i) as Keyframe[], opts));
    });
    root.querySelectorAll<HTMLElement>(".hp-tap").forEach((el, i) => {
      const tap = track.taps[i];
      if (tap) anims.push(el.animate(tapFrames(tap) as Keyframe[], opts));
    });
    const cursor = root.querySelector<HTMLElement>(".hp-cursor");
    if (cursor) anims.push(cursor.animate(cursorFrames(track.taps) as Keyframe[], opts));
    for (const a of anims) {
      a.currentTime = startAt * 1000;
      // A screen that starts quiet holds still until its moment.
      if (pausedRef.current) a.pause?.();
    }
    animsRef.current = anims;
    return () => {
      for (const a of anims) a.cancel();
      animsRef.current = [];
    };
  }, [track, reduced, startAt]);
  // Play or hold; a new `seekTo` jumps to that moment first.
  useEffect(() => {
    for (const a of animsRef.current) {
      if (seekTo !== undefined) a.currentTime = seekTo * 1000;
      if (paused) a.pause?.();
      else a.play?.();
    }
  }, [paused, seekTo]);
  return (
    <div className="hp-well" ref={ref}>
      <div className="hp-cam" style={{ aspectRatio: `${track.width} / ${track.height}` }}>
        {track.cuts.map((c, i) => (
          <img
            key={`${c.page.src}-${c.t}`}
            className="hp-page"
            data-first={i === 0 ? "" : undefined}
            data-still={c.still ? "" : undefined}
            src={c.page.src}
            width={track.width}
            height={track.height}
            alt={c.page.alt}
            decoding="async"
            fetchPriority={i === 0 ? "auto" : "low"}
          />
        ))}
        {track.taps.map((t) => (
          <span
            key={t.t}
            className="hp-tap"
            style={{ left: `${(t.fx * 100).toFixed(2)}%`, top: `${(t.fy * 100).toFixed(2)}%` }}
          />
        ))}
        {reduced ? null : (
          <span className="hp-cursor" data-kind={pointer} aria-hidden="true">
            {pointer === "mouse" ? (
              <svg viewBox="0 0 16 22" aria-hidden="true">
                <path
                  d="M1 1v17l4.6-4.3 3 6.8 2.8-1.2-3-6.7H15z"
                  fill="#fff"
                  stroke="#173d3c"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </span>
        )}
      </div>
    </div>
  );
}

export function HomeStillWater({ reduced = false }: { reduced?: boolean } = {}) {
  return (
    <main className="hsw-wrap">
      {/* #2788: the title card sits in the sky of the story, so the headline
      and the day share the first view. */}
      <StoryRun reduced={reduced}>
        <section className="hsw-hero sr-title" aria-labelledby="hsw-title">
          <h1 id="hsw-title">Rostering that keeps everyone in the loop.</h1>
          <p className="hsw-lead">
            Leave and sick days come in from your team's phones.{" "}
            <span className="hsw-keep">
              <TariiWord /> builds
            </span>{" "}
            the roster, you approve it, everyone gets their shifts.
          </p>
          <div className="hsw-cta">
            <Link className="hsw-btn" to="/demo">
              Try the demo
            </Link>
            <Link className="hsw-link" to="/how-it-works">
              See how it works
            </Link>
          </div>
        </section>
      </StoryRun>

      <StorySummary />
    </main>
  );
}
